import {BaseModal} from '../../../../../../components/BaseModal/BaseModal'
import {useEffect, useState} from 'react'
import {
  WuIcon,
  WuSegmentedSelector,
  WuSegmentedSelectorItem,
} from '@npm-questionpro/wick-ui-lib'
import styles from './TopicModal.module.scss'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {DIALOG} from '../../../../../../Translation/English/dialog'

interface ITopicModalProps {
  showModal: boolean
  closeModal: () => void
  data: {
    topic: string
    count: number
    responses?: Array<{response: string; sentiment: string}>
  } | null
}

export const TopicModal: React.FC<ITopicModalProps> = ({
  showModal,
  closeModal,
  data,
}) => {
  const wt = useWt()
  const [selectedValue, setSelectedValue] = useState('all')
  const [selectedResponses, setSelectedResponses] = useState(
    data?.responses || [],
  )

  useEffect(() => {
    if (data) {
      if (selectedValue === 'all') {
        setSelectedResponses(data.responses || [])
      } else {
        setSelectedResponses(
          data.responses?.filter(
            response =>
              response.sentiment.toLocaleLowerCase() === selectedValue,
          ) || [],
        )
      }
    }
  }, [selectedValue, data])

  const getLabel = (sentiment: string): React.ReactElement => {
    const count =
      sentiment === 'all'
        ? data?.responses?.length
        : data?.responses?.filter(
            response => response.sentiment.toLocaleLowerCase() === sentiment,
          ).length

    return (
      <>
        <div className={styles.labelText}>
          {wt(DIALOG[sentiment as keyof typeof DIALOG] ?? sentiment)}
        </div>
        <div className={styles.labelCount}>{count}</div>
      </>
    )
  }

  const radioOptions = [
    {value: 'all', label: getLabel('all')},
    {value: 'positive', label: getLabel('positive')},
    {value: 'negative', label: getLabel('negative')},
    {value: 'neutral', label: getLabel('neutral')},
  ]

  const getHeader = (): React.ReactElement => {
    return (
      <div className={styles.title}>
        {wt(DIALOG.topic)}: {data?.topic}
      </div>
    )
  }

  const getIcon = (sentiment: string): React.ReactElement | null => {
    switch (sentiment) {
      case 'Positive':
        return (
          <WuIcon
            icon="wm-sentiment-satisfied"
            className={styles.responsesGreen}
          />
        )
      case 'Negative':
        return (
          <WuIcon
            icon="wm-sentiment-dissatisfied"
            className={styles.responsesRed}
          />
        )
      case 'Neutral':
        return (
          <WuIcon
            icon="wm-sentiment-neutral"
            className={styles.responsesYellow}
          />
        )
      default:
        return null
    }
  }

  return (
    <BaseModal
      visible={showModal}
      headerTitle={getHeader()}
      onCloseModal={() => closeModal()}
      maxWidth="750px"
    >
      <div className={styles.topicModal}>
        <div className={styles.subtitle}>{wt(DIALOG.responses)}</div>
        <div className={styles.radioGroup}>
          <WuSegmentedSelector
            value={selectedValue}
            onChange={setSelectedValue}
          >
            {radioOptions.map(option => (
              <WuSegmentedSelectorItem
                key={option.value}
                value={option.value}
                className={styles.label}
              >
                {option.label}
              </WuSegmentedSelectorItem>
            ))}
          </WuSegmentedSelector>
        </div>
        <div className={styles.responses}>
          {selectedResponses?.map((response, index) => (
            <div key={index} className={styles.responsesItem}>
              <div className={styles.responsesSentiment}>
                {getIcon(response.sentiment)}
              </div>
              <div className={styles.responsesText}>{response.response}</div>
            </div>
          ))}
        </div>
      </div>
    </BaseModal>
  )
}
